import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';

/**
 * Everything the page holds lives in the query string.
 *
 * Not a nicety: a report is a thing people SEND each other ("run this for Nashik, last quarter"), and
 * with the state in React only, the URL was `/reports` no matter what was on screen — so a link was
 * worthless and the browser Back button dropped you out of the page rather than back to the previous
 * report. Filter keys vary per report, so they share one `f.` prefix rather than a hook per key.
 *
 * THE PARAMETER NAMES ARE A CONTRACT. `r`, `from`, `to`, `k`, `area` and `f.<key>` are what every
 * bookmarked and shared report URL in the wild already carries; the redesign around this hook changed
 * how the page looks, never what a link means.
 */
export const FILTER_PREFIX = 'f.';

export const DEFAULT_FROM = () => dayjs().subtract(29, 'day').format('YYYY-MM-DD');
export const DEFAULT_TO = () => dayjs().format('YYYY-MM-DD');

export function useReportUrlState() {
  const [params, setParams] = useSearchParams();

  const code = params.get('r') ?? undefined;
  const area = params.get('area') ?? undefined;
  // The measure the table is ordered by. In the URL with everything else, so a link to a report
  // arrives ordered the way the sender was reading it (see the url-backed-filters rule).
  const kpiSort = params.get('k') || null;
  /*
   * The period as the URL states it. `hasPeriod` is false when the link carries no dates at all —
   * then the REPORT decides what it opens on (its `defaultPeriod`: last 30 days, this FY, all time),
   * because one 30-day default silently emptied every "what is owed / still open" report. An empty
   * value (`from=`) is a deliberate open end: "all time" is a real choice, not a missing one.
   */
  const hasPeriod = params.has('from') || params.has('to');
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';

  const filters = useMemo(() => {
    const out: Record<string, any> = {};
    params.forEach((v, k) => {
      if (!k.startsWith(FILTER_PREFIX) || v === '') return;
      const key = k.slice(FILTER_PREFIX.length);
      // A repeated param is a multi-select; `getAll` keeps both halves of `f.channel=WEB&f.channel=MOBILE`.
      const all = params.getAll(k).filter((x) => x !== '');
      out[key] = all.length > 1 ? all : all[0];
    });
    return out;
  }, [params]);

  const patch = useCallback((fn: (p: URLSearchParams) => void) => {
    setParams((prev) => { const next = new URLSearchParams(prev); fn(next); return next; }, { replace: true });
  }, [setParams]);

  const setCode = useCallback((c: string | undefined) => patch((p) => {
    // Filters belong to the report that declared them — carrying `f.batchNo` into a complaints report
    // would post a parameter its SQL never reads and quietly imply a filter that isn't applied.
    [...p.keys()].filter((k) => k.startsWith(FILTER_PREFIX)).forEach((k) => p.delete(k));
    // …and the sort, which names a COLUMN of the report being left behind. A period the reader PICKED
    // stays (comparing two reports for the same month is a normal thing to do); an untouched period is
    // not in the URL at all, so the next report opens on its own default.
    p.delete('k');
    if (c) p.set('r', c); else p.delete('r');
  }), [patch]);

  // Both ends are always written — an empty one as `from=` — so a cleared date means "open end"
  // rather than falling back to the report's default the moment it is cleared.
  const setRange = useCallback((f: string, t: string) => patch((p) => {
    p.set('from', f ?? '');
    p.set('to', t ?? '');
  }), [patch]);

  const patchFilters = useCallback((entries: Record<string, any>) => patch((p) => {
    for (const [key, value] of Object.entries(entries)) {
      const k = FILTER_PREFIX + key;
      p.delete(k);
      if (value === undefined || value === null || value === '') continue;
      if (Array.isArray(value)) value.forEach((v) => p.append(k, String(v)));
      else p.set(k, String(value));
    }
  }), [patch]);

  const setFilter = useCallback(
    (key: string, value: any) => patchFilters({ [key]: value }),
    [patchFilters],
  );

  const clearFilters = useCallback(() => patch((p) => {
    [...p.keys()].filter((k) => k.startsWith(FILTER_PREFIX)).forEach((k) => p.delete(k));
  }), [patch]);

  const setArea = useCallback((a: string | undefined) => patch((p) => {
    if (a) p.set('area', a); else p.delete('area');
  }), [patch]);

  const setKpiSort = useCallback((k: string | null) => patch((p) => {
    if (k) p.set('k', k); else p.delete('k');
  }), [patch]);

  return {
    code, area, from, to, hasPeriod, filters, kpiSort,
    setCode, setArea, setRange, setFilter, patchFilters, clearFilters, setKpiSort,
  };
}
